import { format, isValid, parse, parseISO, setHours, setMinutes } from '@e3dc-react/shell/Libs/date'
import { LineItem } from '../models'
import { SmartMeterDocument } from '../models/system-configure-checkout-overview.model'
import { ProductGeneralType, ProductType } from '../types'

interface SmartMeterDocumentProps {
  smartMeterId: string
  smartMeterDocuments: SmartMeterDocument[] | null
}

export interface SmartMeterCartQuantity {
  smartMeterQuantity: number
  smartMeterDocumentsQuantity: number
}

export interface SmartMeterContractDate {
  date: string
  time: string
}

export interface SmartMeterTimeOptions {
  value: string
  label: string
}

/**
 * This function gets smart meter documents in the cart products if there is any
 */
export const getSmartMeterCartDocumentsFromSystemConfigurator = (lineItems: LineItem[], smartMeterId: string): SmartMeterDocumentProps | null => {
  const systemConfigurationsCart = lineItems.filter(product => product.type === ProductGeneralType.SYSTEM_CONFIGURATOR)
  for (const configurator of systemConfigurationsCart) {
    const smartMeterProductInConfigurator = configurator.children.find(product => product.id === smartMeterId)
    if (smartMeterProductInConfigurator) {
      return checkProductForSmartMetersAvailable(configurator)
    }
  }

  return null
}

/**
 * This function gets smart meter documents from Cart for a specific SmartMeter Id
 */
export const getSmartMeterIdDocumentsFromCart = (lineItems: LineItem[], smartMeterId: string): SmartMeterDocumentProps | null => {
  const product = lineItems.find(product => product.id === smartMeterId)

  if (!product) {
    const smartMeterIntoCart = getSmartMeterCartDocumentsFromSystemConfigurator(lineItems, smartMeterId)
    if (smartMeterIntoCart?.smartMeterId === smartMeterId) {
      return smartMeterIntoCart
    }
  }

  if (product) {
    return checkProductForSmartMetersAvailable(product)
  }
  return null
}

/**
 * This function checks if there is any smart meter availabale on the specific product
 */
export const checkProductForSmartMetersAvailable = (lineItem: LineItem): SmartMeterDocumentProps | null => {
  let smartMeterId: string | null = null
  let smartMeterDocuments: SmartMeterDocument[] | null = null

  const systemConfigurator = lineItem?.type === ProductGeneralType.SYSTEM_CONFIGURATOR
  const normalProduct = lineItem?.type === ProductGeneralType.PRODUCT

  if (systemConfigurator) {
    const smartMeterCart = lineItem?.children?.find(product => product.payload.e3dc?.productType === ProductType.SmartMeter)
    smartMeterId = smartMeterCart?.id ?? null
    smartMeterDocuments = smartMeterCart?.payload?.sm_contracts ?? null
  } else if (normalProduct) {
    const smartMeterCart = lineItem.payload.e3dc?.productType === ProductType.SmartMeter ? lineItem : null
    smartMeterId = smartMeterCart?.id ?? null
    smartMeterDocuments = smartMeterCart?.payload?.sm_contracts ?? null
  }

  if (smartMeterId) {
    return {
      smartMeterId,
      smartMeterDocuments,
    }
  }

  return null
}

/**
 * This function gets the smart meter quantity information from the configured system
 */
export const getSmartMeterQuantityInformation = (lineItem: LineItem): SmartMeterCartQuantity | null => {
  let smartMeterCart = null

  if (lineItem?.children) {
    smartMeterCart = lineItem.children.find(product => product.payload.e3dc?.productType === ProductType.SmartMeter)
  }

  if (!smartMeterCart && lineItem.payload.e3dc?.productType === ProductType.SmartMeter) {
    smartMeterCart = lineItem
  }
  const smartMeterQuantity = smartMeterCart?.quantity
  const smartMeterDocumentsQuantity = smartMeterCart?.payload?.sm_contracts ? Object.keys(smartMeterCart.payload.sm_contracts).length : 0
  if (smartMeterQuantity) {
    return {
      smartMeterQuantity,
      smartMeterDocumentsQuantity,
    }
  }
  return null
}

/*
 * This function check on the cart if the quantity of smartmeters is same as contracts uploaded.
 */
export const isSmartMeterQuantitySameAsDocuments = (lineItems: LineItem[]): boolean => {
  const systemConfigurationCart = lineItems.filter(product => product.type === ProductGeneralType.SYSTEM_CONFIGURATOR)
  const smartMeters = lineItems.filter(product => product.payload.e3dc?.productType === ProductType.SmartMeter)

  for (const systemConfiguration of systemConfigurationCart) {
    const systemConfigQuantityInfo = getSmartMeterQuantityInformation(systemConfiguration)

    if (systemConfigQuantityInfo && systemConfigQuantityInfo.smartMeterQuantity !== systemConfigQuantityInfo.smartMeterDocumentsQuantity) {
      return false
    }
  }

  for (const smartMeter of smartMeters) {
    const smartMeterQuantityInfo = getSmartMeterQuantityInformation(smartMeter)

    if (smartMeterQuantityInfo?.smartMeterQuantity !== smartMeterQuantityInfo?.smartMeterDocumentsQuantity) {
      return false
    }
  }
  return true
}

/*
 * This function returns separated date from time for smart meters
 */
export const extractSmartMeterDate = (dateTime: string): SmartMeterContractDate => {
  if (!dateTime) return { date: '', time: '' }

  const parsed = parseISO(dateTime)

  return {
    date: format(parsed, 'yyyy-MM-dd'),
    time: format(parsed, 'HH:mm'),
  }
}

/*
 * This function merge date with time for smart meters
 */
export const combineSmartMeterDateTime = (date: string, time: string): string => {
  if (!date?.trim() || !time?.trim()) return ''

  let parsedDate = parseISO(date)
  if (!isValid(parsedDate)) {
    parsedDate = parse(date, 'yyyy-MM-dd', new Date())
  }

  const merged = parse(`${format(parsedDate, 'yyyy-MM-dd')} ${time}`, 'yyyy-MM-dd HH:mm', new Date())

  if (!isValid(merged)) return ''

  return format(merged, 'yyyy-MM-dd HH:mm')
}

/*
 * This function generates the hours for the target installation time
 */
export const generateSmartMeterTimeOptions = (): SmartMeterTimeOptions[] => {
  const startHour = 8
  const endHour = 16
  const options = []

  for (let hour = startHour; hour <= endHour; hour++) {
    const date = setMinutes(setHours(new Date(), hour), 0)
    const timeStr = format(date, 'HH:mm')

    options.push({ value: timeStr, label: timeStr })
  }

  return options
}
